import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import { JsonLd } from '../../components/JsonLd';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';

export function LinkedInGuide() {
  usePageMetadata({
    title: 'LinkedIn Headshot Guide: What Recruiters Actually Look For — PhotoFeeler AI',
    description:
      'Professional headshot guide for LinkedIn in 2026. Learn what recruiters evaluate, how to get the right shot, and how to test your photo before publishing.',
    canonicalPath: '/guides/linkedin',
  });

  const jsonLdData = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'LinkedIn Headshot Guide: What Recruiters Actually Look For',
      description:
        'A five-step guide to choosing a LinkedIn headshot that projects competence, approachability, and professionalism to recruiters.',
      step: [
        {
          '@type': 'HowToStep',
          name: 'Understand what recruiters evaluate',
          text: 'Recruiters spend 19% of their LinkedIn viewing time on your photo. They assess competence, approachability, and whether you look current.',
        },
        {
          '@type': 'HowToStep',
          name: 'Test your current headshot',
          text: 'Upload your existing LinkedIn photo to PhotoFeeler AI to see how it scores on competence, influence, and likability before making changes.',
        },
        {
          '@type': 'HowToStep',
          name: 'Get the technical details right',
          text: 'Use a square crop at 640x640px minimum, fill 60% of the frame with your face, and use a clean neutral background.',
        },
        {
          '@type': 'HowToStep',
          name: 'Nail the expression and attire',
          text: 'Wear what you would actually wear to work. Smile naturally. Authenticity outperforms formality in modern professional contexts.',
        },
        {
          '@type': 'HowToStep',
          name: 'Compare options with objective data',
          text: 'Upload your top 2-3 headshot options to PhotoFeeler AI, compare scores, and publish the one that scores highest on competence and approachability.',
        },
      ],
    }),
    []
  );

  return (
    <>
      <Navbar />
      <JsonLd data={jsonLdData} />
      <main className="relative overflow-hidden animate-fade-in">
        <div className="page-orb left-[-5rem] top-24 h-72 w-72 bg-info/10" />
        <div className="page-orb right-[-6rem] top-28 h-80 w-80 bg-rose/10" />

        {/* Hero */}
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-28 pb-4">
          <Link
            to="/blog"
            className="text-xs font-sans text-ink-faint hover:text-rose transition-colors mb-6 inline-block"
          >
            &larr; Blog
          </Link>
          <div className="page-hero-panel mb-12">
            <p className="section-kicker !text-left mb-3">LinkedIn Headshot Guide</p>
            <h1
              className="font-serif text-3xl sm:text-5xl text-ink mb-5"
              style={{ fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.1 }}
            >
              LinkedIn Headshot Guide: What Recruiters Actually Look&nbsp;For
            </h1>
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-sans font-medium text-info tracking-widest uppercase">
                LinkedIn
              </span>
              <span className="text-[10px] text-ink-faint">&middot;</span>
              <span className="text-[10px] text-ink-faint font-sans">8 min read</span>
              <span className="text-[10px] text-ink-faint">&middot;</span>
              <span className="text-[10px] text-ink-faint font-sans">March 2026</span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pb-20">
          <div className="marketing-panel p-6 sm:p-10">
            <div className="prose-custom">
              <p>
                An eye-tracking study of thirty professional recruiters found that they spend{' '}
                <strong>19% of their total LinkedIn viewing time</strong> on a single element:
                your profile photo. Not your headline. Not your experience section. Not your
                skills endorsements. Your face. And <strong>67% of recruiters</strong> say they
                will not message a candidate whose photo looks unprofessional.
              </p>
              <p>
                LinkedIn&apos;s own data confirms it: profiles with a professional photo receive{' '}
                <strong>21 times more views</strong> and <strong>9 times more connection
                requests</strong> than those without one. Your headshot is, by the numbers, the
                single most impactful element on your professional online presence. This guide
                covers exactly what &ldquo;professional&rdquo; means in 2026 and how to get it
                right.
              </p>

              <h2>1. What Recruiters Actually Evaluate in Your Photo</h2>
              <p>
                When a recruiter lands on your profile, their brain processes your photo in a
                fraction of a second and forms snap judgments on three dimensions:
              </p>
              <ul>
                <li>
                  <strong>Competence</strong> &mdash; &ldquo;Does this person look like they
                  know what they are doing?&rdquo; Clean grooming, appropriate attire, and good
                  posture signal competence.
                </li>
                <li>
                  <strong>Approachability</strong> &mdash; &ldquo;Would I want to work with this
                  person?&rdquo; A natural smile and warm lighting signal approachability. Stiff,
                  arms-crossed poses do the opposite.
                </li>
                <li>
                  <strong>Currency</strong> &mdash; &ldquo;Is this what they actually look like
                  today?&rdquo; Outdated photos erode trust the moment someone sees you on a
                  video call and you look noticeably different.
                </li>
              </ul>
              <p>
                The important insight is that &ldquo;professional&rdquo; no longer means
                &ldquo;formal.&rdquo; The era of the dark-suited, studio-lit, corporate headshot
                is over for most industries. In 2026, a warm, approachable photo that genuinely
                looks like you outperforms a stiff, over-produced one.
              </p>

              <h2>2. Score Your Current Photo Before Changing Anything</h2>
              <p>
                Before investing time or money in a new headshot, find out how your current one
                actually performs. Most people have never received objective feedback on their
                LinkedIn photo&mdash;they chose it years ago, thought it looked &ldquo;fine,&rdquo;
                and never revisited it.
              </p>
              <p>
                Not sure if your current photo is helping or hurting?{' '}
                <Link to="/linkedin" className="text-rose hover:underline font-medium">
                  Upload it to PhotoFeeler
                </Link>{' '}
                and see how it scores on competence, influence, and likability. Three independent
                AI models give you an objective baseline in about fifteen seconds. You might
                discover your current photo is stronger than you thought&mdash;or that a change
                is overdue.
              </p>

              <h2>3. The Technical Checklist for a Strong Headshot</h2>
              <p>
                Even the best expression and outfit will underperform if the technical fundamentals
                are wrong. Here is what LinkedIn&apos;s own guidelines and recruiter surveys
                recommend:
              </p>
              <ul>
                <li>
                  <strong>Resolution:</strong> At least 640 x 640 pixels. LinkedIn crops to a
                  circle, so compose with that in mind&mdash;keep your head centered and avoid
                  putting important details near the corners.
                </li>
                <li>
                  <strong>Face coverage:</strong> Your face should fill approximately 60% of the
                  frame. Too far away and you become unrecognizable in the tiny thumbnail. Too
                  close and it feels uncomfortably intimate.
                </li>
                <li>
                  <strong>Background:</strong> Clean, neutral, and uncluttered. A solid color, a
                  slightly blurred office, or an outdoor setting with bokeh all work. A messy room,
                  a bar, or a cropped vacation photo does not.
                </li>
                <li>
                  <strong>Lighting:</strong> Soft, directional light is the single most important
                  technical factor. Window light (with you facing the window) is free and
                  universally flattering. Avoid overhead fluorescents, on-camera flash, and direct
                  midday sun.
                </li>
                <li>
                  <strong>Format:</strong> PNG preserves more detail than JPEG, especially around
                  the edges of your face against the background. LinkedIn accepts files up to 8 MB.
                </li>
              </ul>

              <h2>4. Expression and Attire: What Actually Works</h2>
              <p>
                The smile question comes up constantly. The answer, backed by perception research,
                is straightforward: yes, smile. A natural, slight smile makes you look approachable
                without undermining authority. The key word is <em>natural</em>&mdash;not the
                &ldquo;say cheese&rdquo; grimace, but the expression you would have if a colleague
                told a mildly funny joke. Think &ldquo;pleasant conversation,&rdquo; not
                &ldquo;photo day.&rdquo;
              </p>
              <p>
                For attire, the rule is simpler than most people think: wear what you would
                actually wear on a normal workday. If you are in tech and wear a crew-neck tee
                to the office, a tee is fine. If you are in finance and wear a blazer, wear a
                blazer. Authenticity matters more than formality. The mismatch between &ldquo;how
                you look on LinkedIn&rdquo; and &ldquo;how you look on Zoom&rdquo; is what
                damages credibility.
              </p>
              <p>
                Color guidance: stick to classic neutrals&mdash;navy, black, white, charcoal, or
                grey. Loud patterns pull attention away from your face, which is the opposite of
                what you want. Your face should be the focal point. Everything else is supporting
                context.
              </p>

              <h2>5. Compare Your Options With Objective Data</h2>
              <p>
                If you are getting a new headshot taken, have the photographer give you three to
                five final options rather than picking one on the spot. Small differences in
                expression, angle, and lighting create surprisingly large differences in how
                you are perceived.
              </p>
              <p>
                The problem is that you cannot objectively evaluate your own face. Research
                consistently shows that people pick less effective photos of themselves compared
                to what strangers select. Your self-perception is shaped by familiarity, mood,
                and memories associated with the photo&mdash;none of which a recruiter shares.
              </p>
              <p>
                Ready to test?{' '}
                <Link to="/linkedin" className="text-rose hover:underline font-medium">
                  Upload your top 2&ndash;3 headshot options to PhotoFeeler
                </Link>{' '}
                and compare the scores side by side. The AI models evaluate competence, influence,
                and likability&mdash;the exact traits that shape a recruiter&apos;s first
                impression. Publish the winner, not the one that feels most familiar.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <div className="marketing-panel-warm p-8 sm:p-10">
              <h3
                className="font-serif text-2xl sm:text-3xl text-ink mb-3"
                style={{ fontWeight: 600 }}
              >
                Test your LinkedIn headshot
              </h3>
              <p
                className="text-sm text-ink-faint mb-6 max-w-md mx-auto"
                style={{ fontWeight: 300 }}
              >
                See how recruiters will perceive your photo before you publish it.
                Three AI models, fifteen seconds, real scores on competence and approachability.
              </p>
              <Link to="/linkedin" className="btn-rose text-sm inline-block">
                Upload your LinkedIn headshot &rarr;
              </Link>
            </div>
          </div>

          {/* Related reading */}
          <div className="mt-16 pt-10 border-t border-surface-border">
            <p className="section-kicker !text-left mb-6">Related Reading</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/linkedin-headshot-guide" className="card lift-hover block">
                <span className="text-[10px] font-sans font-medium text-info tracking-widest uppercase">
                  LinkedIn
                </span>
                <h4 className="font-serif text-lg text-ink mt-2" style={{ fontWeight: 600 }}>
                  What Recruiters Actually See When They Look at Your LinkedIn Photo
                </h4>
                <p className="text-xs text-ink-faint mt-2">5 min read</p>
              </Link>
              <Link to="/guides/hinge" className="card lift-hover block">
                <span className="text-[10px] font-sans font-medium text-rose tracking-widest uppercase">
                  Guide
                </span>
                <h4 className="font-serif text-lg text-ink mt-2" style={{ fontWeight: 600 }}>
                  How to Choose the Best Hinge Photos (2026 Guide)
                </h4>
                <p className="text-xs text-ink-faint mt-2">8 min read</p>
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
